import { useState, useEffect, useMemo } from "react";
import { ItemYield } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader } from "lucide-react";
import { createId } from "@paralleldrive/cuid2";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Decimal from "decimal.js";

interface YieldEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yields: ItemYield[];
  onSave: (yields: ItemYield[]) => Promise<void>;
}

interface LocalYield {
  id: string;
  percentage: string;
  minQty?: number;
  isQuantityOverride: boolean;
}

function validateRates(rates: LocalYield[]): string | null {
  const seen = new Set<string>();

  for (const rate of rates) {
    if (rate.isQuantityOverride && rate.minQty === undefined) {
      return "Quantity override must have a quantity value";
    }

    const key = rate.minQty !== undefined ? String(rate.minQty) : "default";
    if (seen.has(key)) {
      return rate.minQty !== undefined
        ? `Duplicate yield at quantity ${rate.minQty}`
        : "Duplicate default yield";
    }
    seen.add(key);

    const pct = new Decimal(rate.percentage || "0");
    if (pct.lessThanOrEqualTo(0) || pct.greaterThan(100)) {
      return `Yield percentage must be between 0% and 100%. Got: ${pct.toString()}%`;
    }
  }

  return null;
}

export function YieldEditorSheet({
  open,
  onOpenChange,
  yields,
  onSave,
}: YieldEditorSheetProps) {
  const [localRates, setLocalRates] = useState<LocalYield[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleOpenChange = (newOpen: boolean) => {
    if (isSaving) {
      return;
    }
    onOpenChange(newOpen);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const converted = yields.map((r) => ({
      id: createId(),
      percentage: r.percentage,
      minQty: r.minQty,
      isQuantityOverride: r.minQty !== undefined,
    }));

    if (converted.length === 0) {
      setLocalRates([
        {
          id: createId(),
          percentage: "100",
          isQuantityOverride: false,
        },
      ]);
    } else {
      setLocalRates(converted);
    }
    setValidationError(null);
  }, [open, yields]);

  const defaultRate = useMemo(
    () => localRates.find((r) => !r.isQuantityOverride),
    [localRates],
  );

  const overrideRates = useMemo(
    () => localRates.filter((r) => r.isQuantityOverride),
    [localRates],
  );

  const handlePercentageChange = (id: string, percentage: string) => {
    setLocalRates(
      localRates.map((r) => (r.id === id ? { ...r, percentage } : r)),
    );
  };

  const handleMinQtyChange = (id: string, minQty?: number) => {
    setLocalRates(localRates.map((r) => (r.id === id ? { ...r, minQty } : r)));
  };

  const handleRemoveOverride = (id: string) => {
    setLocalRates(localRates.filter((r) => r.id !== id));
  };

  const handleAddQuantityOverride = () => {
    const existingMinQtys = overrideRates
      .map((r) => r.minQty)
      .filter((q): q is number => q !== undefined);
    const maxQty = Math.max(0, ...existingMinQtys);

    const newRate: LocalYield = {
      id: createId(),
      percentage: "100",
      minQty: maxQty + 10,
      isQuantityOverride: true,
    };
    setLocalRates([...localRates, newRate]);
  };

  const handleSave = async () => {
    const filteredRates = localRates.filter(
      (r) => r.percentage && r.percentage !== "100",
    );

    const validationErrorMsg = validateRates(filteredRates);
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }

    setIsSaving(true);
    setValidationError(null);

    const newYields: ItemYield[] = filteredRates.map((r) => ({
      percentage: r.percentage,
      ...(r.minQty !== undefined ? { minQty: r.minQty } : {}),
    }));

    await onSave(newYields);
    setIsSaving(false);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Edit Yield</SheetTitle>
          <SheetDescription>
            Configure yield percentages. Yield increases production quantity to
            account for waste during manufacturing (e.g., 95% yield means 5%
            will be scrapped).
          </SheetDescription>
          {validationError && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-3 py-2 rounded-md text-sm">
              {validationError}
            </div>
          )}
        </SheetHeader>

        <div className="flex justify-between items-center py-4 flex-shrink-0 border-b">
          <Label>Yields</Label>
        </div>

        <div className="grid gap-4 py-4 flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-3">
              {defaultRate && (
                <div className="bg-muted/50 rounded-md p-3 space-y-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    Default (All Quantities)
                  </span>
                  <div className="grid gap-3 grid-cols-1">
                    <div className="space-y-2">
                      <Label className="text-xs">Yield (%)</Label>
                      <Input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={defaultRate.percentage}
                        onChange={(e) =>
                          handlePercentageChange(defaultRate.id, e.target.value)
                        }
                        placeholder="100"
                        disabled={isSaving}
                        className="h-8 bg-background"
                      />
                    </div>
                  </div>
                </div>
              )}

              {overrideRates.length > 0 && (
                <div className="space-y-2 pl-4 border-l-2 border-border">
                  <span className="text-xs font-medium text-muted-foreground">
                    Quantity Overrides
                  </span>
                  {overrideRates.map((override) => (
                    <div
                      key={override.id}
                      className="bg-accent rounded-md p-3 space-y-3"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-accent-foreground">
                          At Quantity {override.minQty || 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOverride(override.id)}
                          disabled={isSaving}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="grid gap-3 grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={override.minQty?.toString() ?? ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const numValue =
                                inputValue === ""
                                  ? undefined
                                  : parseInt(inputValue, 10);
                              if (numValue !== undefined) {
                                handleMinQtyChange(override.id, numValue);
                              }
                            }}
                            placeholder="1"
                            disabled={isSaving}
                            className="h-8 bg-background"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Yield (%)</Label>
                          <Input
                            type="number"
                            min="0.01"
                            max="100"
                            step="0.01"
                            value={override.percentage}
                            onChange={(e) =>
                              handlePercentageChange(
                                override.id,
                                e.target.value,
                              )
                            }
                            placeholder="100"
                            disabled={isSaving}
                            className="h-8 bg-background"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddQuantityOverride}
                disabled={isSaving}
                className="w-full"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Quantity Override
              </Button>
            </div>
          </div>
        </div>

        <SheetFooter className="flex-shrink-0">
          <SheetClose asChild>
            <Button variant="outline" disabled={isSaving}>
              Cancel
            </Button>
          </SheetClose>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            Save Yield
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
